import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, Edit2, Trash2, Calendar, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SEO } from "@/components/seo/SEO";
import { FamilyMemberDialog } from "@/components/family/FamilyMemberDialog";
import {
  useFamilyMembers,
  RELATIONSHIP_LABELS,
  RELATIONSHIP_ICONS,
  type FamilyMember,
  type FamilyMemberInput,
} from "@/hooks/useFamilyMembers";
import { format, differenceInYears } from "date-fns";
import { fr } from "date-fns/locale";
import { MoreVertical } from "lucide-react";

export default function Family() {
  const navigate = useNavigate();
  const { members, isLoading, createMember, updateMember, deleteMember } = useFamilyMembers();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [deletingMember, setDeletingMember] = useState<FamilyMember | null>(null);

  const handleCreate = async (data: FamilyMemberInput) => {
    await createMember.mutateAsync(data);
  };

  const handleUpdate = async (data: FamilyMemberInput) => {
    if (!editingMember) return;
    await updateMember.mutateAsync({ id: editingMember.id, ...data });
  };

  const handleDelete = async () => {
    if (!deletingMember) return;
    await deleteMember.mutateAsync(deletingMember.id);
    setDeletingMember(null);
  };

  const openEdit = (member: FamilyMember) => {
    setEditingMember(member);
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditingMember(null);
    setDialogOpen(true);
  };

  const getAge = (birthDate: string | null) => {
    if (!birthDate) return null;
    return differenceInYears(new Date(), new Date(birthDate));
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl py-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="grid gap-4 sm:grid-cols-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Ma Famille - EasyFinder"
        description="Gérez les documents administratifs de votre famille"
      />

      <div className="container max-w-4xl py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6 text-primary" />
              Ma Famille
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez les documents de santé de chaque membre
            </p>
          </div>
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Ajouter un membre
          </Button>
        </div>

        {/* Members Grid */}
        {members.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-1">Aucun membre</h3>
              <p className="text-muted-foreground mb-4">
                Ajoutez les membres de votre famille pour gérer leurs documents de santé
              </p>
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Ajouter un membre
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {members.map((member) => {
              const age = getAge(member.birth_date);
              const initials = `${member.first_name.charAt(0)}${member.last_name.charAt(0)}`.toUpperCase();

              return (
                <Card
                  key={member.id}
                  className="hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/family/${member.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                          {initials}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold truncate">
                              {member.first_name} {member.last_name}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="gap-1">
                                <span>{RELATIONSHIP_ICONS[member.relationship]}</span>
                                {RELATIONSHIP_LABELS[member.relationship]}
                              </Badge>
                              {age !== null && (
                                <span className="text-sm text-muted-foreground">
                                  {age} ans
                                </span>
                              )}
                            </div>
                          </div>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/family/${member.id}`);
                                }}
                              >
                                <FileText className="mr-2 h-4 w-4" />
                                Voir les documents
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEdit(member);
                                }}
                              >
                                <Edit2 className="mr-2 h-4 w-4" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingMember(member);
                                }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        {member.birth_date && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            <span>
                              Né(e) le {format(new Date(member.birth_date), "d MMMM yyyy", { locale: fr })}
                            </span>
                          </div>
                        )}

                        {member.notes && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-1">
                            {member.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <FamilyMemberDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        member={editingMember}
        onSubmit={editingMember ? handleUpdate : handleCreate}
        isLoading={createMember.isPending || updateMember.isPending}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingMember} onOpenChange={() => setDeletingMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce membre ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingMember && (
                <>
                  Voulez-vous vraiment supprimer {deletingMember.first_name} {deletingMember.last_name} ?
                  Tous les documents liés seront détachés.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
